/* eslint-disable react/jsx-key */
/**
 * Shared venue element renderer. Mirrors the rich rendering logic from
 * VenueEditor so that the public/event-detail viewers display sections with
 * the exact same style (classic, modern, futuristic, neon, glass,
 * brutalist, holographic, luxury, minimalist) and shape (rect, circle,
 * hexagon, triangle, diamond, star, trapezoid, arc, lshape, rounded).
 *
 * Includes type-specific decorations for bar (counter + bottles), djbooth
 * (turntables + mixer), stage (apron + spotlights), table (chairs), lounge
 * (cushions + VIP plate), and all guide icons (entrance, exit, bathroom,
 * stairs, elevator, wardrobe, smoking, firstaid, info, parking, foodcourt,
 * wheelchair).
 *
 * Keep this in sync with VenueEditor.jsx if new styles/shapes are added.
 */
import React from 'react';

export const STYLES = [
  { id: 'classic', label: 'Clásico', gradient: false, pattern: 'none', opacity: 0.7 },
  { id: 'modern', label: 'Moderno', gradient: true, pattern: 'dots', opacity: 0.75, gradientType: 'diagonal' },
  { id: 'minimalist', label: 'Minimalista', gradient: false, pattern: 'none', opacity: 0.5, stroke: 'dashed' },
  { id: 'futuristic', label: 'Futurista', gradient: true, pattern: 'grid', opacity: 0.6, glow: true },
  { id: 'neon', label: 'Neón', gradient: false, pattern: 'none', opacity: 0.4, glow: true, neonBorder: true },
  { id: 'glass', label: 'Cristal', gradient: false, pattern: 'none', opacity: 0.3, blur: true },
  { id: 'brutalist', label: 'Brutalista', gradient: false, pattern: 'noise', opacity: 0.85, thickBorder: true, roughEdge: true },
  { id: 'holographic', label: 'Holográfico', gradient: true, pattern: 'holo', opacity: 0.6, holographic: true },
  { id: 'luxury', label: 'Luxury', gradient: true, pattern: 'metallic', opacity: 0.9, metallic: true, goldTrim: true },
];

export const ELEMENT_TYPES = {
  section:     { hasCapacity: true,  category: 'main' },
  stage:       { hasCapacity: false, category: 'main' },
  djbooth:     { hasCapacity: false, category: 'main' },
  bar:         { hasCapacity: true,  category: 'main' },
  table:       { hasCapacity: true,  category: 'main' },
  lounge:      { hasCapacity: true,  category: 'main' },
  wall:        { category: 'structure', color: '#52525B' },
  wall_thick:  { category: 'structure', color: '#3F3F46' },
  wall_thin:   { category: 'structure', color: '#71717A' },
  wall_dashed: { category: 'structure', color: '#A1A1AA' },
  corner_wall: { category: 'structure', color: '#52525B' },
  pillar:      { category: 'structure', color: '#3F3F46' },
  circle_deco: { category: 'structure', color: '#71717A' },
  rect_deco:   { category: 'structure', color: '#71717A' },
  area:        { category: 'structure', color: '#27272A' },
  exit:        { category: 'guide', color: '#22C55E' },
  entrance:    { category: 'guide', color: '#3B82F6' },
  bathroom:    { category: 'guide', color: '#06B6D4' },
  stairs:      { category: 'guide', color: '#8B5CF6' },
  elevator:    { category: 'guide', color: '#6366F1' },
  wardrobe:    { category: 'guide', color: '#EC4899' },
  smoking:     { category: 'guide', color: '#78716C' },
  firstaid:    { category: 'guide', color: '#EF4444' },
  info:        { category: 'guide', color: '#EAB308' },
  parking:     { category: 'guide', color: '#1F2937' },
  foodcourt:   { category: 'guide', color: '#F97316' },
  wheelchair:  { category: 'guide', color: '#0EA5E9' },
};

export const getShapePath = (shape, width, height) => {
  const w = width, h = height;
  switch (shape) {
    case 'rect': return `M 4 4 L ${w-4} 4 L ${w-4} ${h-4} L 4 ${h-4} Z`;
    case 'rounded': { const r = Math.min(16, w/4, h/4); return `M ${r+4} 4 L ${w-r-4} 4 Q ${w-4} 4 ${w-4} ${r+4} L ${w-4} ${h-r-4} Q ${w-4} ${h-4} ${w-r-4} ${h-4} L ${r+4} ${h-4} Q 4 ${h-4} 4 ${h-r-4} L 4 ${r+4} Q 4 4 ${r+4} 4 Z`; }
    case 'circle': return `M ${w/2} 4 A ${w/2-4} ${h/2-4} 0 1 1 ${w/2} ${h-4} A ${w/2-4} ${h/2-4} 0 1 1 ${w/2} 4 Z`;
    case 'hexagon': return `M ${w*0.25} 4 L ${w*0.75} 4 L ${w-4} ${h/2} L ${w*0.75} ${h-4} L ${w*0.25} ${h-4} L 4 ${h/2} Z`;
    case 'triangle': return `M ${w/2} 4 L ${w-4} ${h-4} L 4 ${h-4} Z`;
    case 'diamond': return `M ${w/2} 4 L ${w-4} ${h/2} L ${w/2} ${h-4} L 4 ${h/2} Z`;
    case 'star': { let p = ''; for (let i = 0; i < 10; i++) { const r2 = i % 2 === 0 ? Math.min(w,h)/2-4 : (Math.min(w,h)/2-4)*0.4; const a = (i*Math.PI/5)-Math.PI/2; p += (i===0?'M ':'L ')+(w/2+r2*Math.cos(a))+' '+(h/2+r2*Math.sin(a))+' '; } return p+'Z'; }
    case 'trapezoid': return `M ${w*0.15} ${h-4} L ${w*0.05} 4 L ${w*0.95} 4 L ${w*0.85} ${h-4} Z`;
    case 'arc': return `M 4 ${h} A ${w/2-4} ${h-8} 0 0 1 ${w-4} ${h} L ${w*0.85} ${h} A ${w*0.35} ${h*0.7} 0 0 0 ${w*0.15} ${h} Z`;
    case 'lshape': return `M 4 4 L ${w*0.4} 4 L ${w*0.4} ${h*0.5} L ${w-4} ${h*0.5} L ${w-4} ${h-4} L 4 ${h-4} Z`;
    default: return `M 4 4 L ${w-4} 4 L ${w-4} ${h-4} L 4 ${h-4} Z`;
  }
};

/**
 * Rich element renderer matching the VenueEditor output. Renders the section
 * exactly as the producer designed it (style, shape, glow, gradient, pattern,
 * type-specific decorations). `isSelectable` controls hover/click affordances;
 * `isSelected` adds a selection ring.
 */
export const RichElementShape = ({ element, isSelected = false, isSelectable = true, onClick }) => {
  const { type = 'section', color = '#4F46E5', name = '', capacity = 0, shape = 'rect', style = 'modern', width, height } = element;
  const styleConfig = STYLES.find(s => s.id === style) || STYLES[0];
  const shapePath = getShapePath(shape, width, height);

  const opacityMultiplier = isSelectable ? 1 : 0.45;

  const getStyledFill = () => {
    if (styleConfig.holographic || styleConfig.metallic) return `url(#grad-rich-${element.id})`;
    if (styleConfig.gradient || styleConfig.gradientType) return `url(#grad-rich-${element.id})`;
    return `${color}${Math.round(styleConfig.opacity * opacityMultiplier * 255).toString(16).padStart(2, '0')}`;
  };
  const getStyledStroke = () => {
    if (isSelected) return '#22c55e';
    if (styleConfig.neonBorder) return color;
    if (styleConfig.goldTrim) return '#D4AF37';
    if (styleConfig.thickBorder) return color;
    return `${color}cc`;
  };
  const getStrokeWidth = () => {
    if (isSelected) return 4;
    if (styleConfig.thickBorder) return 4;
    if (styleConfig.neonBorder) return 3;
    if (styleConfig.goldTrim) return 2;
    return 2;
  };

  const renderStyleDefs = () => (
    <defs>
      {styleConfig.gradientType === 'diagonal' && (
        <linearGradient id={`grad-rich-${element.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity={(styleConfig.opacity + 0.15) * opacityMultiplier} />
          <stop offset="50%" stopColor={color} stopOpacity={styleConfig.opacity * opacityMultiplier} />
          <stop offset="100%" stopColor={color} stopOpacity={(styleConfig.opacity - 0.2) * opacityMultiplier} />
        </linearGradient>
      )}
      {styleConfig.glow && !styleConfig.neonBorder && !styleConfig.holographic && (
        <linearGradient id={`grad-rich-${element.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity={(styleConfig.opacity + 0.2) * opacityMultiplier} />
          <stop offset="50%" stopColor={color} stopOpacity={(styleConfig.opacity - 0.1) * opacityMultiplier} />
          <stop offset="100%" stopColor={color} stopOpacity={(styleConfig.opacity + 0.1) * opacityMultiplier} />
        </linearGradient>
      )}
      {styleConfig.neonBorder && (
        <linearGradient id={`grad-rich-${element.id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity={(styleConfig.opacity + 0.1) * opacityMultiplier} />
          <stop offset="100%" stopColor={color} stopOpacity={(styleConfig.opacity - 0.1) * opacityMultiplier} />
        </linearGradient>
      )}
      {styleConfig.blur && (
        <linearGradient id={`grad-rich-${element.id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity={0.15 * opacityMultiplier} />
          <stop offset="100%" stopColor={color} stopOpacity={0.2 * opacityMultiplier} />
        </linearGradient>
      )}
      {styleConfig.roughEdge && (
        <linearGradient id={`grad-rich-${element.id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity={0.95 * opacityMultiplier} />
          <stop offset="100%" stopColor={color} stopOpacity={0.85 * opacityMultiplier} />
        </linearGradient>
      )}
      {styleConfig.holographic && (
        <linearGradient id={`grad-rich-${element.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#ec4899" stopOpacity={0.7 * opacityMultiplier} />
          <stop offset="20%" stopColor="#8b5cf6" stopOpacity={0.7 * opacityMultiplier} />
          <stop offset="40%" stopColor="#06b6d4" stopOpacity={0.7 * opacityMultiplier} />
          <stop offset="60%" stopColor="#22c55e" stopOpacity={0.7 * opacityMultiplier} />
          <stop offset="80%" stopColor="#eab308" stopOpacity={0.7 * opacityMultiplier} />
          <stop offset="100%" stopColor="#ec4899" stopOpacity={0.7 * opacityMultiplier} />
        </linearGradient>
      )}
      {styleConfig.metallic && (
        <linearGradient id={`grad-rich-${element.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.95 * opacityMultiplier} />
          <stop offset="25%" stopColor={color} stopOpacity={0.85 * opacityMultiplier} />
          <stop offset="50%" stopColor="#FFD700" stopOpacity={0.6 * opacityMultiplier} />
          <stop offset="75%" stopColor={color} stopOpacity={0.85 * opacityMultiplier} />
          <stop offset="100%" stopColor="#B8860B" stopOpacity={0.9 * opacityMultiplier} />
        </linearGradient>
      )}
      {styleConfig.gradient && !styleConfig.gradientType && !styleConfig.glow && !styleConfig.blur && !styleConfig.roughEdge && !styleConfig.holographic && !styleConfig.metallic && !styleConfig.neonBorder && (
        <linearGradient id={`grad-rich-${element.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity={(styleConfig.opacity + 0.2) * opacityMultiplier} />
          <stop offset="100%" stopColor={color} stopOpacity={(styleConfig.opacity - 0.2) * opacityMultiplier} />
        </linearGradient>
      )}
      {styleConfig.pattern === 'dots' && <pattern id={`pat-rich-${element.id}`} width="8" height="8" patternUnits="userSpaceOnUse"><circle cx="4" cy="4" r="1.5" fill="rgba(255,255,255,0.15)"/></pattern>}
      {styleConfig.pattern === 'grid' && <pattern id={`pat-rich-${element.id}`} width="12" height="12" patternUnits="userSpaceOnUse"><path d="M 12 0 L 0 0 0 12" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="0.8"/></pattern>}
      {styleConfig.pattern === 'noise' && <pattern id={`pat-rich-${element.id}`} width="6" height="6" patternUnits="userSpaceOnUse"><rect x="0" y="0" width="2" height="2" fill="rgba(255,255,255,0.08)"/><rect x="3" y="2" width="2" height="2" fill="rgba(0,0,0,0.15)"/><rect x="1" y="4" width="2" height="2" fill="rgba(255,255,255,0.05)"/><rect x="4" y="0" width="2" height="2" fill="rgba(0,0,0,0.1)"/></pattern>}
      {styleConfig.pattern === 'holo' && <pattern id={`pat-rich-${element.id}`} width="16" height="16" patternUnits="userSpaceOnUse"><line x1="0" y1="0" x2="16" y2="16" stroke="rgba(255,255,255,0.15)" strokeWidth="0.8"/><line x1="16" y1="0" x2="0" y2="16" stroke="rgba(255,255,255,0.1)" strokeWidth="0.8"/><circle cx="8" cy="8" r="3" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="0.5"/></pattern>}
      {styleConfig.pattern === 'metallic' && <pattern id={`pat-rich-${element.id}`} width="4" height="4" patternUnits="userSpaceOnUse"><line x1="0" y1="2" x2="4" y2="2" stroke="rgba(212,175,55,0.3)" strokeWidth="1"/></pattern>}
      {(styleConfig.glow || styleConfig.neonBorder) && (
        <filter id={`glow-rich-${element.id}`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      )}
    </defs>
  );

  const renderStyleOverlays = (path) => (
    <>
      {styleConfig.pattern && styleConfig.pattern !== 'none' && <path d={path} fill={`url(#pat-rich-${element.id})`}/>}
      {styleConfig.neonBorder && <path d={path} fill="none" stroke={color} strokeWidth="6" opacity="0.2" filter={`url(#glow-rich-${element.id})`}/>}
      {styleConfig.glow && !styleConfig.neonBorder && <path d={path} fill="none" stroke={color} strokeWidth="4" opacity="0.15" filter={`url(#glow-rich-${element.id})`}/>}
      {styleConfig.metallic && <path d={path} fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="1" transform="scale(0.95) translate(3,3)"/>}
      {styleConfig.blur && <rect x="0" y="0" width={width} height={height} fill="rgba(255,255,255,0.05)" rx="8"/>}
    </>
  );

  const fillStyle = getStyledFill();
  const strokeStyle = getStyledStroke();
  const strokeWidth = getStrokeWidth();
  const dashArray = styleConfig.stroke === 'dashed' ? '6 3' : 'none';

  const handleClick = isSelectable ? onClick : undefined;
  const cursorClass = isSelectable ? 'cursor-pointer hover:brightness-110' : 'cursor-not-allowed';
  const wrapperOpacity = isSelectable ? 1 : 0.6;
  const selectedBadge = isSelected ? (
    <g transform={`translate(${width-18}, 4)`} style={{ pointerEvents: 'none' }}>
      <circle cx="8" cy="8" r="8" fill="#22c55e"/>
      <path d="M 4 8 L 7 11 L 12 5" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </g>
  ) : null;

  // ===== BAR =====
  if (type === 'bar') {
    return (
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className={`transition-all ${cursorClass}`} onClick={handleClick} style={{ overflow: 'visible', opacity: wrapperOpacity }}>
        {renderStyleDefs()}
        <rect x={4} y={height*0.3} width={width-8} height={height*0.65} rx={6} fill={fillStyle} stroke={strokeStyle} strokeWidth={strokeWidth} strokeDasharray={dashArray}/>
        {renderStyleOverlays(`M 4 ${height*0.3+6} Q 4 ${height*0.3} 10 ${height*0.3} L ${width-10} ${height*0.3} Q ${width-4} ${height*0.3} ${width-4} ${height*0.3+6} L ${width-4} ${height*0.95-6} Q ${width-4} ${height*0.95} ${width-10} ${height*0.95} L 10 ${height*0.95} Q 4 ${height*0.95} 4 ${height*0.95-6} Z`)}
        <rect x={2} y={height*0.25} width={width-4} height={height*0.12} rx={4} fill={styleConfig.metallic ? '#D4AF37' : color} opacity="0.9"/>
        <rect x={6} y={height*0.28} width={width-12} height={height*0.06} rx={2} fill="rgba(255,255,255,0.3)"/>
        {Math.min(width, height) > 60 && (
          <g>
            {[0.15, 0.3, 0.45, 0.6, 0.75, 0.85].slice(0, Math.max(1, Math.floor(Math.min(width, height)/25))).map((pos, i) => {
              const isVertical = height > width;
              const x = isVertical ? width*0.5 - 4 : width*pos - 4;
              const y = isVertical ? height*pos - 4 : height*0.4;
              const bw = isVertical ? 8 : 8;
              const bh = isVertical ? height*0.06 : height*0.25;
              return (
                <g key={i}>
                  <rect x={x} y={y} width={bw} height={bh} rx={2} fill={styleConfig.holographic ? ['#ec4899','#8b5cf6','#06b6d4','#22c55e','#eab308','#ec4899'][i%6] : ['#22c55e','#ef4444','#3b82f6','#eab308','#8b5cf6','#ec4899'][i%6]} opacity="0.8"/>
                </g>
              );
            })}
          </g>
        )}
        <rect x={width/2-30} y={height*0.85} width={60} height={16} rx={3} fill={styleConfig.metallic ? 'rgba(212,175,55,0.8)' : 'rgba(0,0,0,0.6)'}/>
        <text x={width/2} y={height*0.93} textAnchor="middle" fill="#fff" fontSize={Math.min(10, width/12)} fontWeight="bold" style={{ pointerEvents: 'none' }}>{name}</text>
        {selectedBadge}
      </svg>
    );
  }

  // ===== DJ BOOTH =====
  if (type === 'djbooth') {
    const djPath = shape === 'arc' ? getShapePath('arc', width, height) : shapePath;
    return (
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className={`transition-all ${cursorClass}`} onClick={handleClick} style={{ overflow: 'visible', opacity: wrapperOpacity }}>
        {renderStyleDefs()}
        <path d={djPath} fill={fillStyle} stroke={strokeStyle} strokeWidth={strokeWidth} strokeDasharray={dashArray}/>
        {renderStyleOverlays(djPath)}
        <circle cx={width*0.28} cy={height*0.45} r={Math.min(width*0.12, 14)} fill={styleConfig.metallic ? '#1f1f1f' : '#1f2937'} stroke={styleConfig.goldTrim ? '#D4AF37' : color} strokeWidth="2"/>
        <circle cx={width*0.28} cy={height*0.45} r={Math.min(width*0.06, 7)} fill={styleConfig.holographic ? '#ec4899' : color} opacity="0.6"/>
        <circle cx={width*0.72} cy={height*0.45} r={Math.min(width*0.12, 14)} fill={styleConfig.metallic ? '#1f1f1f' : '#1f2937'} stroke={styleConfig.goldTrim ? '#D4AF37' : color} strokeWidth="2"/>
        <circle cx={width*0.72} cy={height*0.45} r={Math.min(width*0.06, 7)} fill={styleConfig.holographic ? '#06b6d4' : color} opacity="0.6"/>
        <rect x={width*0.38} y={height*0.3} width={width*0.24} height={height*0.35} rx={3} fill={styleConfig.metallic ? '#0f0f0f' : '#1f2937'} stroke={styleConfig.goldTrim ? '#D4AF37' : color} strokeWidth="1"/>
        {[0.35, 0.45, 0.55, 0.65].map((pos, i) => (
          <rect key={i} x={width*0.4+i*width*0.05} y={height*0.35} width={3} height={height*0.2} rx={1} fill={styleConfig.holographic ? ['#ec4899','#8b5cf6','#06b6d4','#22c55e'][i] : color} opacity="0.7"/>
        ))}
        <text x={width/2} y={height*0.85} textAnchor="middle" fill={styleConfig.metallic ? '#D4AF37' : '#fff'} fontSize={Math.min(11, width/12)} fontWeight="bold" style={{ pointerEvents: 'none' }}>{name}</text>
        {selectedBadge}
      </svg>
    );
  }

  // ===== STAGE =====
  if (type === 'stage') {
    const stagePath = shape === 'trapezoid' ? getShapePath('trapezoid', width, height) : shapePath;
    return (
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className={`transition-all ${cursorClass}`} onClick={handleClick} style={{ overflow: 'visible', opacity: wrapperOpacity }}>
        {renderStyleDefs()}
        <path d={stagePath} fill={fillStyle} stroke={strokeStyle} strokeWidth={strokeWidth} strokeDasharray={dashArray}/>
        {renderStyleOverlays(stagePath)}
        <rect x={width*0.05} y={height*0.2} width={width*0.9} height={height*0.1} rx={2} fill={styleConfig.metallic ? '#D4AF37' : '#4B5563'}/>
        {width > 100 && [0.15, 0.3, 0.5, 0.7, 0.85].map((pos, i) => (
          <circle key={i} cx={width*pos} cy={height*0.12} r={5} fill={styleConfig.holographic ? ['#ec4899','#8b5cf6','#06b6d4','#22c55e','#eab308'][i] : (styleConfig.metallic ? '#FFD700' : '#FCD34D')} opacity="0.9"/>
        ))}
        <text x={width/2} y={height*0.65} textAnchor="middle" fill={styleConfig.metallic ? '#D4AF37' : '#fff'} fontSize={Math.min(13, width/10)} fontWeight="bold" style={{ pointerEvents: 'none' }}>{name}</text>
        {selectedBadge}
      </svg>
    );
  }

  // ===== TABLE =====
  if (type === 'table') {
    const chairCount = Math.min(Math.max(4, capacity || 8), 12);
    const tableRadius = Math.min(width, height) * 0.3;
    const chairRadius = Math.min(width, height) * 0.08;
    return (
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className={`transition-all ${cursorClass}`} onClick={handleClick} style={{ overflow: 'visible', opacity: wrapperOpacity }}>
        {renderStyleDefs()}
        {Array.from({ length: chairCount }).map((_, i) => {
          const angle = (i / chairCount) * 2 * Math.PI - Math.PI/2;
          const chairColor = styleConfig.holographic ? ['#ec4899','#8b5cf6','#06b6d4','#22c55e','#eab308','#ec4899','#8b5cf6','#06b6d4','#22c55e','#eab308','#ec4899','#8b5cf6'][i] : color;
          return (
            <circle key={i} cx={width/2 + Math.cos(angle) * (tableRadius + chairRadius + 5)} cy={height/2 + Math.sin(angle) * (tableRadius + chairRadius + 5)} r={chairRadius} fill={`${chairColor}40`} stroke={styleConfig.goldTrim ? '#D4AF37' : chairColor} strokeWidth={1}/>
          );
        })}
        <circle cx={width/2} cy={height/2} r={tableRadius} fill={fillStyle} stroke={strokeStyle} strokeWidth={strokeWidth} strokeDasharray={dashArray}/>
        {styleConfig.pattern && styleConfig.pattern !== 'none' && <circle cx={width/2} cy={height/2} r={tableRadius-2} fill={`url(#pat-rich-${element.id})`}/>}
        {styleConfig.neonBorder && <circle cx={width/2} cy={height/2} r={tableRadius} fill="none" stroke={color} strokeWidth="6" opacity="0.2" filter={`url(#glow-rich-${element.id})`}/>}
        <text x={width/2} y={height/2+4} textAnchor="middle" dominantBaseline="middle" fill={styleConfig.metallic ? '#D4AF37' : '#fff'} fontSize={Math.min(12, tableRadius*0.5)} fontWeight="bold" style={{ pointerEvents: 'none' }}>{(name||'').replace('Mesa ', '')}</text>
        {selectedBadge}
      </svg>
    );
  }

  // ===== LOUNGE / VIP =====
  if (type === 'lounge') {
    return (
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className={`transition-all ${cursorClass}`} onClick={handleClick} style={{ overflow: 'visible', opacity: wrapperOpacity }}>
        {renderStyleDefs()}
        <path d={shapePath} fill={fillStyle} stroke={strokeStyle} strokeWidth={strokeWidth} strokeDasharray={dashArray}/>
        {renderStyleOverlays(shapePath)}
        {height > 50 && <rect x={10} y={height-16} width={width-20} height={8} rx={4} fill={styleConfig.metallic ? 'rgba(212,175,55,0.3)' : 'rgba(255,255,255,0.15)'}/>}
        {height > 50 && <rect x={10} y={8} width={width-20} height={8} rx={4} fill={styleConfig.metallic ? 'rgba(212,175,55,0.3)' : 'rgba(255,255,255,0.15)'}/>}
        <rect x={width/2-20} y={height/2-10} width={40} height={20} rx={3} fill={styleConfig.metallic ? 'rgba(212,175,55,0.9)' : (styleConfig.holographic ? 'rgba(139,92,246,0.8)' : 'rgba(0,0,0,0.5)')}/>
        <text x={width/2} y={height/2+1} textAnchor="middle" dominantBaseline="middle" fill={styleConfig.holographic ? '#fff' : '#D4AF37'} fontSize="10" fontWeight="bold" style={{ pointerEvents: 'none' }}>{name || 'VIP'}</text>
        {capacity > 0 && height > 60 && <text x={width/2} y={height/2+18} textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize="9" style={{ pointerEvents: 'none' }}>{isSelectable ? `${capacity} cap.` : 'Agotado'}</text>}
        {selectedBadge}
      </svg>
    );
  }

  // ===== GUIDE ICONS =====
  const guideTypeConfig = ELEMENT_TYPES[type];
  if (guideTypeConfig?.category === 'guide') {
    const iconColor = guideTypeConfig.color || color;
    const iconSize = Math.min(width, height) * 0.6;
    const cx = width / 2;
    const cy = height / 2 - 4;

    const getGuideIcon = () => {
      switch (type) {
        case 'exit':
          return (
            <>
              <rect x={cx-iconSize/2} y={cy-iconSize/2} width={iconSize} height={iconSize} rx={4} fill={iconColor} opacity="0.2"/>
              <path d={`M ${cx-iconSize*0.3} ${cy} L ${cx+iconSize*0.2} ${cy} M ${cx+iconSize*0.05} ${cy-iconSize*0.15} L ${cx+iconSize*0.2} ${cy} L ${cx+iconSize*0.05} ${cy+iconSize*0.15}`} stroke={iconColor} strokeWidth="3" strokeLinecap="round" fill="none"/>
              <rect x={cx+iconSize*0.25} y={cy-iconSize*0.35} width={iconSize*0.15} height={iconSize*0.7} fill={iconColor} opacity="0.6"/>
            </>
          );
        case 'entrance':
          return (
            <>
              <rect x={cx-iconSize/2} y={cy-iconSize/2} width={iconSize} height={iconSize} rx={4} fill={iconColor} opacity="0.2"/>
              <path d={`M ${cx+iconSize*0.2} ${cy} L ${cx-iconSize*0.2} ${cy} M ${cx-iconSize*0.05} ${cy-iconSize*0.15} L ${cx-iconSize*0.2} ${cy} L ${cx-iconSize*0.05} ${cy+iconSize*0.15}`} stroke={iconColor} strokeWidth="3" strokeLinecap="round" fill="none"/>
            </>
          );
        case 'bathroom':
          return (
            <>
              <rect x={cx-iconSize/2} y={cy-iconSize/2} width={iconSize} height={iconSize} rx={4} fill={iconColor} opacity="0.2"/>
              <circle cx={cx-iconSize*0.18} cy={cy-iconSize*0.2} r={iconSize*0.1} fill={iconColor}/>
              <rect x={cx-iconSize*0.23} y={cy-iconSize*0.05} width={iconSize*0.1} height={iconSize*0.25} rx={2} fill={iconColor}/>
              <path d={`M ${cx-iconSize*0.23} ${cy+iconSize*0.2} L ${cx-iconSize*0.23} ${cy+iconSize*0.35} M ${cx-iconSize*0.13} ${cy+iconSize*0.2} L ${cx-iconSize*0.13} ${cy+iconSize*0.35}`} stroke={iconColor} strokeWidth="2" strokeLinecap="round"/>
              <circle cx={cx+iconSize*0.18} cy={cy-iconSize*0.2} r={iconSize*0.1} fill={iconColor}/>
              <path d={`M ${cx+iconSize*0.08} ${cy+iconSize*0.2} L ${cx+iconSize*0.18} ${cy-iconSize*0.05} L ${cx+iconSize*0.28} ${cy+iconSize*0.2} Z`} fill={iconColor}/>
              <path d={`M ${cx+iconSize*0.13} ${cy+iconSize*0.2} L ${cx+iconSize*0.13} ${cy+iconSize*0.35} M ${cx+iconSize*0.23} ${cy+iconSize*0.2} L ${cx+iconSize*0.23} ${cy+iconSize*0.35}`} stroke={iconColor} strokeWidth="2" strokeLinecap="round"/>
            </>
          );
        case 'stairs':
          return (
            <>
              <rect x={cx-iconSize/2} y={cy-iconSize/2} width={iconSize} height={iconSize} rx={4} fill={iconColor} opacity="0.2"/>
              <path d={`M ${cx-iconSize*0.3} ${cy+iconSize*0.3} L ${cx-iconSize*0.3} ${cy+iconSize*0.1} L ${cx-iconSize*0.1} ${cy+iconSize*0.1} L ${cx-iconSize*0.1} ${cy-iconSize*0.1} L ${cx+iconSize*0.1} ${cy-iconSize*0.1} L ${cx+iconSize*0.1} ${cy-iconSize*0.3} L ${cx+iconSize*0.3} ${cy-iconSize*0.3}`} stroke={iconColor} strokeWidth="3" strokeLinecap="round" fill="none"/>
            </>
          );
        case 'elevator':
          return (
            <>
              <rect x={cx-iconSize/2} y={cy-iconSize/2} width={iconSize} height={iconSize} rx={4} fill={iconColor} opacity="0.2"/>
              <rect x={cx-iconSize*0.35} y={cy-iconSize*0.35} width={iconSize*0.7} height={iconSize*0.7} rx={2} stroke={iconColor} strokeWidth="2" fill="none"/>
              <path d={`M ${cx-iconSize*0.15} ${cy} L ${cx} ${cy-iconSize*0.2} L ${cx+iconSize*0.15} ${cy}`} stroke={iconColor} strokeWidth="2" strokeLinecap="round" fill="none"/>
              <path d={`M ${cx-iconSize*0.15} ${cy+iconSize*0.1} L ${cx} ${cy+iconSize*0.3} L ${cx+iconSize*0.15} ${cy+iconSize*0.1}`} stroke={iconColor} strokeWidth="2" strokeLinecap="round" fill="none"/>
            </>
          );
        case 'wardrobe':
          return (
            <>
              <rect x={cx-iconSize/2} y={cy-iconSize/2} width={iconSize} height={iconSize} rx={4} fill={iconColor} opacity="0.2"/>
              <path d={`M ${cx-iconSize*0.25} ${cy-iconSize*0.25} L ${cx} ${cy-iconSize*0.35} L ${cx+iconSize*0.25} ${cy-iconSize*0.25} L ${cx+iconSize*0.2} ${cy+iconSize*0.25} L ${cx-iconSize*0.2} ${cy+iconSize*0.25} Z`} fill={iconColor} opacity="0.6"/>
              <circle cx={cx} cy={cy-iconSize*0.15} r={iconSize*0.12} fill="none" stroke={iconColor} strokeWidth="2"/>
            </>
          );
        case 'smoking':
          return (
            <>
              <rect x={cx-iconSize/2} y={cy-iconSize/2} width={iconSize} height={iconSize} rx={4} fill={iconColor} opacity="0.2"/>
              <rect x={cx-iconSize*0.35} y={cy} width={iconSize*0.5} height={iconSize*0.12} rx={2} fill={iconColor}/>
              <path d={`M ${cx+iconSize*0.2} ${cy+iconSize*0.06} Q ${cx+iconSize*0.2} ${cy-iconSize*0.15} ${cx+iconSize*0.3} ${cy-iconSize*0.15} Q ${cx+iconSize*0.35} ${cy-iconSize*0.05} ${cx+iconSize*0.35} ${cy+iconSize*0.06}`} stroke={iconColor} strokeWidth="2" fill="none"/>
            </>
          );
        case 'firstaid':
          return (
            <>
              <rect x={cx-iconSize/2} y={cy-iconSize/2} width={iconSize} height={iconSize} rx={4} fill={iconColor} opacity="0.3"/>
              <rect x={cx-iconSize*0.08} y={cy-iconSize*0.3} width={iconSize*0.16} height={iconSize*0.6} rx={2} fill="#fff"/>
              <rect x={cx-iconSize*0.3} y={cy-iconSize*0.08} width={iconSize*0.6} height={iconSize*0.16} rx={2} fill="#fff"/>
            </>
          );
        case 'info':
          return (
            <>
              <circle cx={cx} cy={cy} r={iconSize*0.4} fill={iconColor} opacity="0.2" stroke={iconColor} strokeWidth="2"/>
              <circle cx={cx} cy={cy-iconSize*0.18} r={iconSize*0.06} fill={iconColor}/>
              <rect x={cx-iconSize*0.05} y={cy-iconSize*0.05} width={iconSize*0.1} height={iconSize*0.3} rx={2} fill={iconColor}/>
            </>
          );
        case 'parking':
          return (
            <>
              <rect x={cx-iconSize/2} y={cy-iconSize/2} width={iconSize} height={iconSize} rx={4} fill={iconColor} opacity="0.25"/>
              <text x={cx} y={cy+iconSize*0.15} textAnchor="middle" fill="#fff" fontSize={iconSize*0.6} fontWeight="bold">P</text>
            </>
          );
        case 'foodcourt':
          return (
            <>
              <rect x={cx-iconSize/2} y={cy-iconSize/2} width={iconSize} height={iconSize} rx={4} fill={iconColor} opacity="0.2"/>
              <circle cx={cx} cy={cy} r={iconSize*0.25} stroke={iconColor} strokeWidth="2" fill="none"/>
              <path d={`M ${cx-iconSize*0.1} ${cy-iconSize*0.15} L ${cx-iconSize*0.1} ${cy+iconSize*0.15} M ${cx+iconSize*0.1} ${cy-iconSize*0.15} L ${cx+iconSize*0.1} ${cy+iconSize*0.15}`} stroke={iconColor} strokeWidth="2" strokeLinecap="round"/>
              <path d={`M ${cx-iconSize*0.1} ${cy-iconSize*0.05} L ${cx+iconSize*0.1} ${cy-iconSize*0.05}`} stroke={iconColor} strokeWidth="2"/>
            </>
          );
        case 'wheelchair':
          return (
            <>
              <rect x={cx-iconSize/2} y={cy-iconSize/2} width={iconSize} height={iconSize} rx={4} fill={iconColor} opacity="0.2"/>
              <circle cx={cx-iconSize*0.05} cy={cy-iconSize*0.2} r={iconSize*0.1} fill={iconColor}/>
              <path d={`M ${cx-iconSize*0.05} ${cy-iconSize*0.08} L ${cx-iconSize*0.05} ${cy+iconSize*0.1} L ${cx+iconSize*0.15} ${cy+iconSize*0.1}`} stroke={iconColor} strokeWidth="2" strokeLinecap="round" fill="none"/>
              <circle cx={cx-iconSize*0.05} cy={cy+iconSize*0.2} r={iconSize*0.15} stroke={iconColor} strokeWidth="2" fill="none"/>
            </>
          );
        default:
          return <rect x={cx-iconSize/2} y={cy-iconSize/2} width={iconSize} height={iconSize} rx={4} fill={iconColor} opacity="0.3"/>;
      }
    };

    return (
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ overflow: 'visible', pointerEvents: 'none', opacity: wrapperOpacity }}>
        {getGuideIcon()}
        <text x={width/2} y={height - 6} textAnchor="middle" fill="#fff" fontSize={Math.min(10, width/10)} fontWeight="500" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.8)' }}>{name}</text>
      </svg>
    );
  }

  // ===== STRUCTURE =====
  const structCfg = ELEMENT_TYPES[type];
  if (structCfg?.category === 'structure') {
    const structColor = color || structCfg.color || '#52525B';
    if (type === 'wall' || type === 'wall_thick') {
      const thickness = Math.min(height, type === 'wall_thick' ? 16 : 8);
      return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ pointerEvents: 'none' }}>
          <rect x="0" y={(height - thickness) / 2} width={width} height={thickness} rx={2} fill={structColor} />
        </svg>
      );
    }
    if (type === 'wall_thin') return <svg width={width} height={height}><line x1="0" y1={height/2} x2={width} y2={height/2} stroke={structColor} strokeWidth="2" strokeLinecap="round"/></svg>;
    if (type === 'wall_dashed') return <svg width={width} height={height}><line x1="0" y1={height/2} x2={width} y2={height/2} stroke={structColor} strokeWidth="3" strokeDasharray="8 6" strokeLinecap="round"/></svg>;
    if (type === 'corner_wall') {
      const t = 8;
      return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ pointerEvents: 'none' }}>
          <path d={`M 0 0 L ${t} 0 L ${t} ${height - t} L ${width} ${height - t} L ${width} ${height} L 0 ${height} Z`} fill={structColor}/>
        </svg>
      );
    }
    if (type === 'pillar') { const r = Math.min(width, height) / 2 - 1; return <svg width={width} height={height}><circle cx={width/2} cy={height/2} r={r} fill={structColor}/><circle cx={width/2} cy={height/2} r={r*0.6} fill="rgba(0,0,0,0.25)"/></svg>; }
    if (type === 'circle_deco') return <svg width={width} height={height}><circle cx={width/2} cy={height/2} r={Math.min(width, height)/2 - 2} fill="none" stroke={structColor} strokeWidth="2" strokeDasharray="6 3"/></svg>;
    if (type === 'rect_deco') return <svg width={width} height={height}><rect x="2" y="2" width={width - 4} height={height - 4} rx={4} fill="none" stroke={structColor} strokeWidth="2" strokeDasharray="8 4"/></svg>;
    if (type === 'area') return (
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ pointerEvents: 'none' }}>
        <rect x="0" y="0" width={width} height={height} rx={4} fill={structColor} opacity="0.15"/>
        <rect x="0" y="0" width={width} height={height} rx={4} fill="none" stroke={structColor} strokeWidth="1" strokeDasharray="4 2" opacity="0.4"/>
        <text x={width/2} y={height/2} textAnchor="middle" dominantBaseline="middle" fill={structColor} fontSize={Math.min(11, width/10)} opacity="0.6">{name}</text>
      </svg>
    );
  }

  // ===== DEFAULT (section) =====
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={`transition-all ${cursorClass}`}
      onClick={handleClick}
      style={{ overflow: 'visible', opacity: wrapperOpacity }}
    >
      {renderStyleDefs()}
      <path
        d={shapePath}
        fill={fillStyle}
        stroke={strokeStyle}
        strokeWidth={strokeWidth}
        strokeDasharray={dashArray}
      />
      {renderStyleOverlays(shapePath)}
      <text
        x={width/2}
        y={height/2 - (capacity > 0 ? 6 : 0)}
        textAnchor="middle"
        dominantBaseline="middle"
        fill={styleConfig.metallic ? '#D4AF37' : '#fff'}
        fontSize={Math.min(12, width/10)}
        fontWeight="bold"
        style={{ textShadow: '0 1px 3px rgba(0,0,0,0.8)', pointerEvents: 'none' }}
      >
        {name}
      </text>
      {capacity > 0 && (
        <text
          x={width/2}
          y={height/2 + 10}
          textAnchor="middle"
          fill={isSelectable ? 'rgba(255,255,255,0.75)' : 'rgba(255,255,255,0.4)'}
          fontSize={Math.min(10, width/12)}
          style={{ pointerEvents: 'none' }}
        >
          {isSelectable ? `${capacity} cap.` : 'Agotado'}
        </text>
      )}
      {selectedBadge}
    </svg>
  );
};

export default RichElementShape;
